// The action bar, as a poker client has one: chips to size the bet, what it pays, and the two
// actions. While a bet is riding at this table it holds one thing only: the cash out.

import { useState } from "react";
import { useConnection } from "wagmi";
import type { Instrument, Position, Refusal } from "../../sdk";
import { Button } from "../../components/ui";
import { Roll } from "../../components/Roll";
import { ConnectButton } from "../../wallet/ConnectButton";
import { navigate } from "../../router";
import { useAccount, useStreamStatus } from "../../state/account";
import { enableTrading, useInvalidateKey, useKeyState } from "../../state/actions";
import { useStore } from "../../state/store";
import { REFUSAL } from "../../lib/errors";
import { big, fromRaw, toRaw, usd } from "../../lib/num";
import { pct } from "../../odds/model";
import { placePlay, usePlays } from "../../play/book";
import { buzz } from "../../play/hooks";
import { sound } from "../../play/sound";
import type { Quote } from "../../play/quote";
import { CHIPS, TARGETS, setSlip, slip as slipStore, type Call } from "../../play/slip";
import { CashOut, useWorth } from "./Tickets";

const WORDS: Partial<Record<Refusal, string>> = {
  noMargin: "Tap a chip to bet",
  badAmount: "Tap a chip to bet",
  belowMinimumMargin: "Below the table minimum",
  belowMinimumNotional: "Below the table minimum",
  aboveMaximumPosition: "Above the table limit",
  insufficientFunds: "Not enough chips",
  marketClosed: "Table closed",
  intakePaused: "Table paused",
  tradingPaused: "Table paused",
};

const raw = (dollars: string) => toRaw(dollars || "0", 18) ?? 0n;

/** The bet riding at this table: what it is worth now, and the way out. */
function Riding({ position }: { position: Position }) {
  const account = useAccount();
  const plays = usePlays();
  const worth = useWorth(position);
  const play = plays.find((item) => item.positionId === position.positionId);
  const closing = (account?.operations ?? []).some((op) => op.action === "closePositions" && op.positionIds.includes(position.positionId));
  return (
    <div className="dock dock-riding">
      <div className={`riding ${worth ? (worth.up ? "is-up" : "is-down") : ""}`}>
        <span className="riding-mult num">{worth ? worth.multiple : "—"}</span>
        <span className="riding-sub num">
          {position.isLong ? "▲ Up" : "▼ Down"} · {usd(position.margin)}
          {play?.target ? ` · to ${play.target}×` : ""}
        </span>
      </div>
      <CashOut position={position} play={play} closing={closing} className="cash cash-lg" />
    </div>
  );
}

export function Dock({
  instrument,
  quotes,
  leverage,
  focus,
  onPreview,
}: {
  instrument: Instrument;
  quotes: Record<Call, Quote | null>;
  leverage: number;
  focus: Position | null;
  onPreview: (call: Call | null) => void;
}) {
  const { status } = useConnection();
  const account = useAccount();
  const stream = useStreamStatus();
  const keyState = useKeyState();
  const invalidateKey = useInvalidateKey();
  const plays = usePlays();
  const state = useStore(slipStore);
  const [busy, setBusy] = useState<Call | "key" | null>(null);
  const target = state.target ?? 2;
  const staked = raw(state.stake);
  const sending = plays.some((play) => play.status === "sent" && play.instrumentId === instrument.id);

  if (focus) return <Riding position={focus} />;

  const tap = () => {
    sound.chip();
    buzz(6);
  };

  const send = async (call: Call) => {
    const q = quotes[call];
    const preview = q?.preview;
    if (busy || !q || preview?.refusal || !preview?.margin) return;
    setBusy(call);
    buzz(20);
    setSlip({ call });
    try {
      await placePlay(
        {
          coin: instrument.symbol,
          instrumentId: instrument.id,
          call,
          stake: preview.margin,
          leverage,
          target,
          targetBp: q.targetBp,
          targetMark: q.targetMark,
          targetPayout: q.targetPayout?.toString() ?? null,
          clock: null,
        },
        state.stake,
      );
      sound.placed();
    } catch {
      // told by a toast
    } finally {
      setBusy(null);
    }
  };

  const shown = quotes[state.call];
  const available = big(shown?.preview.available);
  let actions: React.ReactNode;
  if (status !== "connected") actions = <ConnectButton size="lg" block label="Take a seat" />;
  else if (!account)
    actions = (
      <Button size="lg" block disabled busy={stream !== "live"}>
        Opening your account
      </Button>
    );
  else if (keyState.data !== "ready")
    actions = (
      <Button
        tone="primary"
        size="lg"
        block
        busy={busy === "key" || keyState.isLoading}
        onClick={async () => {
          setBusy("key");
          try {
            await enableTrading();
          } catch {
            // told by a toast
          } finally {
            setBusy(null);
            void invalidateKey();
          }
        }}
      >
        Turn on one-tap bets
      </Button>
    );
  else if (!account.hasDeposited && available === 0n)
    actions = (
      <Button tone="primary" size="lg" block onClick={() => navigate("/wallet")}>
        Buy in
      </Button>
    );
  else if (sending)
    actions = (
      <div className="dock-sending">
        <span className="spinner" aria-hidden /> Dealing you in
      </div>
    );
  else {
    const refusalOf = (call: Call): Refusal | null => (staked > 0n ? (quotes[call]?.preview.refusal ?? "loading") : "noMargin");
    const refusal = refusalOf(state.call);
    actions = (
      <div className="acts">
        {(["down", "up"] as const).map((call) => (
          <button
            key={call}
            type="button"
            className={`act act-${call}`}
            disabled={refusalOf(call) !== null || busy !== null}
            onClick={() => void send(call)}
            onPointerEnter={() => onPreview(call)}
            onPointerLeave={() => onPreview(null)}
            onFocus={() => onPreview(call)}
            onBlur={() => onPreview(null)}
          >
            {busy === call ? (
              <span className="spinner" aria-hidden />
            ) : (
              <>
                <span className="act-arrow" aria-hidden>
                  {call === "up" ? "▲" : "▼"}
                </span>
                {call === "up" ? "Up" : "Down"}
              </>
            )}
          </button>
        ))}
        <div className="odds" aria-live="polite">
          {refusal && refusal !== "loading" ? (
            <span className="odds-why">{WORDS[refusal] ?? REFUSAL[refusal]}</span>
          ) : (
            <>
              <span className="odds-win">
                <span className="num">{usd(staked, { places: 2, min: 0 })}</span>
                <span className="odds-to" aria-hidden>
                  →
                </span>
                {shown?.targetPayout ? <Roll className="odds-pay" text={usd(shown.targetPayout, { places: 2, min: 0 })} /> : <span className="odds-pay">—</span>}
              </span>
              <span className="odds-chance num" title={shown?.odds.rtp != null ? `Return to player ${Math.round(shown.odds.rtp * 100)}%` : undefined}>
                {shown?.odds.target != null ? `${pct(shown.odds.target)} chance` : ""}
              </span>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="dock">
      <div className="dock-row">
        <div className="rack" role="group" aria-label="Chips">
          {CHIPS.map((chip) => (
            <button
              key={chip}
              type="button"
              className={`pchip pchip-${chip}`}
              onClick={() => {
                tap();
                setSlip({ stake: fromRaw(staked + raw(chip), 18) });
              }}
              aria-label={`Add a $${chip} chip`}
            >
              <span>{chip}</span>
            </button>
          ))}
          <button
            type="button"
            className="rack-clear"
            disabled={staked === 0n}
            onClick={() => {
              tap();
              setSlip({ stake: "" });
            }}
            aria-label="Take your chips back"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
              <path d="M3.5 8a4.5 4.5 0 1 0 1.4-3.3M3.5 2.5v2.4h2.4" />
            </svg>
          </button>
        </div>
        <div className="pays" role="radiogroup" aria-label="Pays">
          {TARGETS.map((multiple) => (
            <button
              key={multiple}
              type="button"
              role="radio"
              aria-checked={target === multiple}
              className="num"
              onClick={() => {
                tap();
                setSlip({ target: multiple });
              }}
            >
              {multiple}×
            </button>
          ))}
        </div>
      </div>
      {actions}
    </div>
  );
}
